import { StrictMode } from "react";
import * as Sentry from "@sentry/react";
import {
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { createRoot } from "react-dom/client";
import { Helmet } from "react-helmet";

import { ErrorBoundaryFallback } from "~/common/screens/ErrorBoundaryFallback";

import "~/common/index.css";

import ReactDOM from "react-dom";
import { BrowserRouter } from "react-router-dom";
import { z } from "zod";

import { Toasts, useToastStore } from "~/common/components/toast";
import { env } from "./env";
import { Router } from "./router";

import "react-toastify/dist/ReactToastify.css";
import "react-loading-skeleton/dist/skeleton.css";

import { NuqsAdapter } from "nuqs/adapters/react-router/v6";
import { ToastContainer } from "react-toastify";

const errorSchema = z.object({ message: z.string() });

const isLocal = env.VITE_APP_ENV === "local";

const queryCache = !isLocal
  ? new QueryCache()
  : new QueryCache({
      // here we set a generic error handler on dev mode
      onError: (e, query) => {
        const parsedError = errorSchema.safeParse(e);
        const { pushToast } = useToastStore.getState();

        void pushToast({
          type: "error",
          title: "Error",
          message: `${query.queryKey[0] as string} error: ${
            !parsedError.success
              ? "Whoops! Please check the network tab in the dev tools"
              : parsedError.data.message
          }`,
        });
      },
    });

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
    },
  },
  queryCache,
});

const root = document.getElementById("root");

if (!root) {
  throw new Error(
    "There's no #root div, something's wrong with our index.html",
  );
}

// TO DO: Change to different DSN
Sentry.init({
  dsn: env.VITE_SENTRY_DSN,
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration({
      maskAllText: false,
      blockAllMedia: false,
    }),
  ],

  tracesSampleRate: 0.1,

  // Set `tracePropagationTargets` to control for which URLs distributed tracing should be enabled
  tracePropagationTargets: [
    new RegExp(env.VITE_SENTRY_TRACE_PROPAGATION_TARGET_REGEX),
  ],

  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
});

createRoot(root).render(
  <StrictMode>
    <Helmet>
      <meta name="robots" content="noindex, nofollow"></meta>
    </Helmet>
    <QueryClientProvider client={queryClient}>
      <Sentry.ErrorBoundary fallback={<ErrorBoundaryFallback />}>
        <BrowserRouter>
          <NuqsAdapter>
            <Router />
          </NuqsAdapter>
        </BrowserRouter>
        <ToastContainer
          position="top-center"
          autoClose={3000}
          hideProgressBar={true}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable={false}
          pauseOnHover
          theme="light"
          className="min-w-25 rounded-sm text-sm"
        />
      </Sentry.ErrorBoundary>

      {ReactDOM.createPortal(<Toasts />, document.body)}
      {isLocal && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  </StrictMode>,
);
